// Components
import React, {ChangeEvent, FormEvent, PureComponent} from 'react'
import {Honeybadger} from '@honeybadger-io/react'
import {
  ComponentColor,
  ComponentSize,
  ComponentStatus,
  FlexBox,
  FlexDirection,
  FontWeight,
  Gradients,
  JustifyContent,
  Method,
  Notification,
  Panel,
  SelectGroup,
  Heading,
  HeadingElement,
} from '@influxdata/clockface'
import CSRF from 'js/components/CSRF'
import {animated, Transition} from 'react-spring/renderprops.cjs'

import {Auth0Connection} from 'js/types'
import LoginForm from './LoginForm'
import MarketplaceLink from '../MarketplaceLink'
import SignUpForm from './SignUpForm'
import SocialSignupButtonsHeader from '../shared/SocialSignupButtonsHeader'
import {
  AnalyticsClient,
  GoogleAnalyticsClient,
} from '../../client/GoogleAnalyticsClient'
import {Auth0Client, AuthenticationClient} from '../../client/Auth0Client'
import {authConnection, AuthConnection} from 'js/client'

interface ErrorObject {
  [key: string]: any
}

export type SignUpMode = 'login' | 'signup'
interface State {
  activeTab: SignUpMode
  buttonStatus: ComponentStatus
  confirmPassword: string
  confirmPasswordError: string
  email: string
  emailError: string
  firstName: string
  firstNameError: string
  lastName: string
  lastNameError: string
  notificationText: string
  notificationVisible: boolean
  password: string
  passwordError: string
}

interface Props {
  mode?: SignUpMode
  email?: string
  analyticsClient?: AnalyticsClient
  authenticationClient?: AuthenticationClient
  authConnection?: AuthConnection
}

class SignUpPageContents extends PureComponent<Props> {
  state: State = {
    activeTab: this.props.mode,
    buttonStatus: ComponentStatus.Default,
    confirmPassword: '',
    confirmPasswordError: '',
    email: this.props.email || '',
    emailError: '',
    firstName: '',
    firstNameError: '',
    lastName: '',
    lastNameError: '',
    notificationText: '',
    notificationVisible: false,
    password: '',
    passwordError: '',
  }

  private authConnection: AuthConnection
  private authClient: AuthenticationClient
  private analyticsClient: AnalyticsClient

  componentDidMount(): void {
    this.authConnection = this.props.authConnection || authConnection
    this.authClient = this.props.authenticationClient || new Auth0Client()
    this.analyticsClient =
      this.props.analyticsClient || new GoogleAnalyticsClient(window.dataLayer)
  }

  render() {
    const {
      buttonStatus,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      firstNameError,
      lastNameError,
      emailError,
      passwordError,
      confirmPasswordError,
      activeTab,
      notificationText,
      notificationVisible,
    } = this.state

    const loginTabActive = activeTab === 'login'

    const hiddenStyles: React.CSSProperties = {height: 0}
    const visibleStyles: React.CSSProperties = {
      position: 'relative',
      overflow: 'hidden',
      height: 'auto',
    }

    return (
      <form
        action="/signup"
        method={Method.Post}
        onSubmit={this.handleSubmit}
        className="sign-up--form"
      >
        <Panel className="sign-up--form-panel">
          <Panel.Header
            size={ComponentSize.Large}
            justifyContent={JustifyContent.Center}
            className="sign-up--form-header"
          >
            <Heading element={HeadingElement.H3}>Continue with</Heading>
          </Panel.Header>
          <Panel.Body size={ComponentSize.Large}>
            <SocialSignupButtonsHeader
              handleSocialClick={this.handleSocialClick}
            />
            <Heading
              element={HeadingElement.H3}
              className="sign-up--form-separator"
            >
              OR
            </Heading>
            <FlexBox
              stretchToFitWidth={true}
              direction={FlexDirection.Row}
              justifyContent={JustifyContent.Center}
            >
              <SelectGroup
                size={ComponentSize.Medium}
                color={ComponentColor.Default}
              >
                <SelectGroup.Option
                  titleText="Log In"
                  value="login"
                  id="login-option"
                  active={loginTabActive}
                  onClick={this.handleTabChange}
                >
                  Log In
                </SelectGroup.Option>
                <SelectGroup.Option
                  titleText="Sign Up"
                  value="signup"
                  id="signup-option"
                  active={!loginTabActive}
                  onClick={this.handleTabChange}
                >
                  Sign Up
                </SelectGroup.Option>
              </SelectGroup>
            </FlexBox>
            <Transition
              native
              reset
              unique
              config={{
                mass: 0.25,
                precision: 1,
                friction: 1,
                clamp: true,
              }}
              items={loginTabActive}
              from={hiddenStyles}
              enter={[visibleStyles]}
              leave={hiddenStyles}
            >
              {(show) =>
                show &&
                ((props) => (
                  <animated.div style={props}>
                    <LoginForm
                      buttonStatus={buttonStatus}
                      email={email}
                      emailError={emailError}
                      password={password}
                      passwordError={passwordError}
                      handleInputChange={this.handleInputChange}
                      handleForgotPasswordClick={this.handleForgotPasswordClick}
                    />
                  </animated.div>
                ))
              }
            </Transition>
            <Transition
              native
              reset
              unique
              config={{
                mass: 0.25,
                precision: 1,
                friction: 1,
                clamp: true,
              }}
              items={loginTabActive === false}
              from={hiddenStyles}
              enter={[visibleStyles]}
              leave={hiddenStyles}
            >
              {(show) =>
                show &&
                ((props) => (
                  <animated.div style={props}>
                    <SignUpForm
                      buttonStatus={buttonStatus}
                      confirmPassword={confirmPassword}
                      confirmPasswordError={confirmPasswordError}
                      email={email}
                      emailError={emailError}
                      firstName={firstName}
                      firstNameError={firstNameError}
                      lastName={lastName}
                      lastNameError={lastNameError}
                      password={password}
                      passwordError={passwordError}
                      handleInputChange={this.handleInputChange}
                    />
                  </animated.div>
                ))
              }
            </Transition>
          </Panel.Body>
          <Notification
            visible={notificationVisible}
            size={ComponentSize.Small}
            gradient={Gradients.HotelBreakfast}
          >
            {notificationText}
          </Notification>
          <CSRF />
          <Heading
            element={HeadingElement.H4}
            weight={FontWeight.Regular}
            className="sign-up--marketplace-footer"
          >
            Or subscribe on{' '}
            <MarketplaceLink
              text="AWS"
              url="https://aws.amazon.com/marketplace/pp/B08234JZPS/?ref=_ptnr_web_influxcloudproducthead"
            />
            ,{' '}
            <MarketplaceLink
              text="Azure"
              url="https://azuremarketplace.microsoft.com/en-us/marketplace/apps/influxdata.influxdb-cloud?ocid=iflxdbcloud_influxdata_web_producthead"
            />
            , or{' '}
            <MarketplaceLink
              text="Google Cloud"
              url="https://console.cloud.google.com/marketplace/details/influxdata-public/cloud2-gcp-marketplace-prod?utm_campaign=influxdb-cloud-product-pagehead&utm_medium=website&utm_source=influxdata"
            />
          </Heading>
        </Panel>
      </form>
    )
  }

  private get validFieldValues(): {
    isValid: boolean
    errors: {[fieldName: string]: string}
  } {
    const {activeTab, firstName, lastName, email, password, confirmPassword} =
      this.state

    const passwordsMatch = confirmPassword === password

    const firstNameError = firstName === '' ? 'First name is required' : ''
    const lastNameError = lastName === '' ? 'Last name is required' : ''
    const emailError = email === '' ? 'Email is required' : ''
    const passwordError = password === '' ? 'Password is required' : ''
    let confirmPasswordError = passwordsMatch
      ? ''
      : "The input passwords don't match"
    if (confirmPassword === '') {
      confirmPasswordError = 'Confirm password is required'
    }

    const errors: ErrorObject = {
      emailError,
      passwordError,
    }
    if (activeTab === 'signup') {
      errors.firstNameError = firstNameError
      errors.lastNameError = lastNameError
      errors.confirmPasswordError = confirmPasswordError
    }

    const isValid = Object.values(errors).every((error) => error === '')

    return {isValid, errors}
  }

  private handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const {
      email,
      password,
      firstName: given_name,
      lastName: family_name,
      activeTab,
    } = this.state

    let connection = ''
    this.setState({buttonStatus: ComponentStatus.Loading})

    try {
      if (email) {
        connection = await this.authConnection(email)
      }
    } catch (error) {
      Honeybadger.notify(error)
      console.error(error)
    }

    if (connection !== '') {
      return this.authenticateWithSSO(connection)
    }

    const {isValid, errors} = this.validFieldValues

    if (!isValid) {
      this.setState({buttonStatus: ComponentStatus.Default})
      this.setState(errors)
      return
    }

    if (activeTab === 'login') {
      this.authClient.login(
        {
          realm: Auth0Connection.Authentication,
          email,
          password,
          redirectTo: 'loginRedirectUri',
        },
        (error) => {
          if (error) {
            this.setState({buttonStatus: ComponentStatus.Default})
            this.displayErrorMessage(errors, error)
            return
          }
        }
      )
      return
    }

    this.authClient.signup(
      {
        connection: Auth0Connection.Authentication,
        email,
        password,
        family_name,
        given_name,
      },
      (error) => {
        if (error) {
          this.displayErrorMessage(errors, error)
          this.setState({buttonStatus: ComponentStatus.Default})
          return
        }
        this.authClient.login({
          realm: Auth0Connection.Authentication,
          email,
          password,
          redirectTo: 'signupRedirectUri',
        })

        this.analyticsClient.send_event('influx_cloud_signup_started', {
          identity_provider: 'email',
        })
      }
    )
  }

  private displayErrorMessage = (errors, auth0Err) => {
    const {activeTab} = this.state
    if (/error in email/.test(auth0Err.code)) {
      this.setState({
        ...errors,
        emailError: 'Please enter a valid email address',
      })
    } else if (auth0Err.code === 'user_exists') {
      const emailError = `The user already exists. Try logging in instead.`
      this.setState({...errors, emailError})
    } else if (
      auth0Err.code === 'invalid_password' ||
      auth0Err.code === 'access_denied' ||
      auth0Err.code === 'extensibility_error'
    ) {
      if (activeTab === 'login') {
        const emailError = `The email and password combination you submitted don't match. Please choose another.`
        this.setState({...errors, emailError})
      } else {
        switch (auth0Err.name) {
          case 'PasswordDictionaryError': {
            const passwordError = `Password is too common. Please choose another.`
            this.setState({...errors, passwordError})
            break
          }
          case 'PasswordNoUserInfoError': {
            const passwordError = `Password contains personal information. Please choose another.`
            this.setState({...errors, passwordError})
            break
          }
          default: {
            const passwordError = `Password is too short. Please choose another.`
            this.setState({...errors, passwordError})
            break
          }
        }
      }
    } else {
      const emailError = `We have been notified of an issue while creating your account. If this issue persists, please contact support@influxdata.com`
      this.setState({...errors, emailError})
      Honeybadger.notify(auth0Err.description || auth0Err.code, auth0Err.name)
    }
  }

  private handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({[e.target.id]: e.target.value})
  }

  private handleTabChange = (value: string) => {
    this.setState({
      activeTab: value,
      buttonStatus: ComponentStatus.Default,
      confirmPassword: '',
      confirmPasswordError: '',
      email: '',
      emailError: '',
      firstName: '',
      firstNameError: '',
      lastName: '',
      lastNameError: '',
      notificationText: '',
      notificationVisible: false,
      password: '',
      passwordError: '',
    })
  }

  private handleSocialClick = (connection: Auth0Connection) => {
    this.authClient.authorize(connection)
  }

  private authenticateWithSSO = (connection: string) => {
    return this.authClient.authorize(connection)
  }

  private handleForgotPasswordClick = () => {
    const {email} = this.state
    if (!email) {
      this.setState({emailError: 'Please enter a valid email address'})
      return
    }
    this.authClient.changePassword(
      {
        email,
        connection: Auth0Connection.Authentication,
      },
      (err, successMessage) => {
        if (err) {
          this.setState({emailError: err.message})
          return
        }
        // notify user that change password email was sent successfully
        // By default auth0 will send a success message even if the operation fails:
        // https://community.auth0.com/t/auth0-changepassword-always-returns-ok-even-when-user-is-not-found/11081/8
        this.setState({
          emailError: '',
          notificationText: `${successMessage}
            If you haven't received an email, please ensure that the email you provided is correct.`,
          notificationVisible: true,
        })
        // sets a time to remove the notification
        setTimeout(() => {
          this.setState({
            notificationText: '',
            notificationVisible: false,
          })
        }, 4000)
      }
    )
  }
}

export {SignUpPageContents}
